import { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'

import { useDoubleTextMotion } from './motion'
import { cn } from '@/lib/utils'

type DoubleTextProps = HTMLAttributes<HTMLDivElement> & {
  pretitle: string
  title: string
  text_left: string
  text_right: string
  has_background: boolean
  title_alignment: 'left' | 'right'
  className?: string
}

const DoubleText = ({
  pretitle,
  title,
  text_left,
  text_right,
  has_background = false,
  title_alignment,
  className,
  ...props
}: DoubleTextProps) => {
  const hasHeader = !!(pretitle || title)
  const headerColClasses = cn('col-span-2 md:col-span-4 lg:col-span-6', 'grid gap-y-spacing-sm', {
    'md:col-start-4 lg:col-start-7': title_alignment === 'right'
  })
  const textColClasses = cn('col-span-2 md:col-span-3 lg:col-span-6', 'flex')
  const { motionRef } = useDoubleTextMotion()

  return (
    <Section ref={motionRef} className={cn('overflow-clip', className)} {...props}>
      <Container>
        <Grid>
          {has_background ? (
            <div
              className={cn('col-[1/-1] row-[1/-1]', 'rounded-radius-minimal bg-white-light/20')}
              data-motion-background
            />
          ) : null}

          <Grid
            className={cn(
              'col-[1/-1] row-[1/-1]',
              'gap-y-spacing-sm md:gap-y-spacing-lg lg:gap-y-spacing-2xl',
              { 'px-spacing-sm py-spacing-lg md:px-spacing-lg': has_background }
            )}
          >
            {hasHeader ? (
              <Grid className="col-span-2 md:col-span-6 lg:col-span-12">
                <div className={headerColClasses}>
                  {pretitle ? (
                    <Typography variant="body" paragraph data-motion-pretitle>
                      {pretitle}
                    </Typography>
                  ) : null}

                  {title ? (
                    <Typography
                      variant="h6"
                      component="h2"
                      className={cn('[&_.line-mask]:-mt-2 [&_.word]:pt-2', {
                        'mix-blend-difference dark:text-text-heading-invert-light': !has_background
                      })}
                      data-motion-title
                    >
                      {title}
                    </Typography>
                  ) : null}
                </div>
              </Grid>
            ) : null}

            {text_left ? (
              <div className={textColClasses}>
                <Typography variant="body" data-motion-l-paragraph>
                  {text_left}
                </Typography>
              </div>
            ) : null}

            {text_right ? (
              <div className={textColClasses}>
                <Typography variant="body" data-motion-r-paragraph>
                  {text_right}
                </Typography>
              </div>
            ) : null}
          </Grid>
        </Grid>
      </Container>
    </Section>
  )
}

export default DoubleText
